// Scales human-written amounts ("1 cup + 1 tbsp", "½ cup", "4 cloves") by a
// factor, keeping the units and the cookbook's fraction style. Amounts with
// no number in them ("to taste", "a small handful") pass through unchanged.
//
// Scaled results are snapped to amounts a person can actually measure. The
// unit decides the rounding: countable things (cloves, eggs, tortillas) become
// whole numbers, teaspoons and pounds land on quarters, cups on eighths. A
// recipe should never ask for 4½ cloves of garlic or 1⅛ cups of soy sauce.

const UNI: Record<string, number> = {
  '½': 1 / 2, '⅓': 1 / 3, '⅔': 2 / 3, '¼': 1 / 4, '¾': 3 / 4,
  '⅕': 1 / 5, '⅖': 2 / 5, '⅗': 3 / 5, '⅘': 4 / 5,
  '⅙': 1 / 6, '⅚': 5 / 6, '⅛': 1 / 8, '⅜': 3 / 8, '⅝': 5 / 8, '⅞': 7 / 8,
};
const UNI_CLASS = Object.keys(UNI).join('');

// A number, plus whatever word follows it — the word is what tells us how to
// round. Groups: 1 mixed, 2 fraction, 3 int+unicode, 4 unicode, 5 decimal,
// 6 the gap before the unit, 7 the unit word.
const TOKEN = new RegExp(
  `(?:(\\d+\\s+\\d+\\s*/\\s*\\d+)|(\\d+\\s*/\\s*\\d+)|(\\d+\\s*[${UNI_CLASS}])|([${UNI_CLASS}])|(\\d+(?:\\.\\d+)?))(\\s*)([a-zA-Z]+)?`,
  'g'
);

function tokenValue(t: string): number {
  const uni = t.match(new RegExp(`^(\\d+)?\\s*([${UNI_CLASS}])$`));
  if (uni) return (uni[1] ? parseInt(uni[1], 10) : 0) + UNI[uni[2]];
  const mixed = t.match(/^(\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (mixed) return parseInt(mixed[1], 10) + parseInt(mixed[2], 10) / parseInt(mixed[3], 10);
  const frac = t.match(/^(\d+)\s*\/\s*(\d+)$/);
  if (frac) return parseInt(frac[1], 10) / parseInt(frac[2], 10);
  return parseFloat(t);
}

// Things you count, not measure. Half of one of these is nonsense.
const COUNTABLE =
  /^(clove|cloves|egg|eggs|tortilla|tortillas|bunch|bunches|stalk|stalks|can|cans|leaf|leaves|link|links|slice|slices|large|medium|small|whole|container|containers|jar|jars|carton|cartons|package|packages|packet|packets|patty|patties|sheet|sheets|strip|strips|piece|pieces|burrito|burritos|serving|servings|bowl|bowls|batch|batches|head|heads|ear|ears|sprig|sprigs|bag|bags|box|boxes)$/i;

type Snap = 'count' | 'quarter' | 'cup' | 'ounce' | 'free';

function snapClass(unit: string | undefined): Snap {
  const u = (unit ?? '').toLowerCase();
  if (!u) return 'count'; // a bare number is a count of something
  if (/^(tsp|teaspoons?|t)$/.test(u)) return 'quarter';
  if (/^(tbsp|tablespoons?|tbs|tb)$/.test(u)) return 'quarter';
  if (/^(cups?|c)$/.test(u)) return 'cup';
  if (/^(lb|lbs|pounds?)$/.test(u)) return 'quarter';
  if (/^(oz|ounces?)$/.test(u)) return 'ounce';
  if (/^(qt|quarts?|pints?|pt|gal|gallons?|l|liters?|litres?)$/.test(u)) return 'quarter';
  if (COUNTABLE.test(u)) return 'count';
  return 'free';
}

function roundTo(v: number, step: number): number {
  const r = Math.round(v / step) * step;
  return r <= 0 ? step : r; // a real amount never rounds away to nothing
}

function snapValue(v: number, unit: string | undefined): number {
  switch (snapClass(unit)) {
    case 'count':
      return Math.max(1, Math.round(v));
    case 'quarter':
      return roundTo(v, 1 / 4);
    case 'cup':
      return v < 1 ? roundTo(v, 1 / 8) : roundTo(v, 1 / 4);
    case 'ounce':
      return v < 4 ? roundTo(v, 1 / 2) : Math.max(1, Math.round(v));
    default:
      return roundTo(v, 1 / 8);
  }
}

// Written-out units have to agree with the number in front of them, or scaled
// prose reads like a robot wrote it: "2 tablespoon of soy sauce". Abbreviations
// (tbsp, oz, lb) never take an s, so they're left alone.
const SINGULARS = [
  'tablespoon', 'teaspoon', 'cup', 'pound', 'ounce', 'clove', 'egg', 'tortilla',
  'bunch', 'stalk', 'can', 'leaf', 'link', 'slice', 'container', 'jar', 'carton',
  'package', 'packet', 'patty', 'sheet', 'strip', 'piece', 'burrito', 'serving',
  'quart', 'pint', 'gallon', 'liter', 'litre', 'head', 'ear', 'sprig', 'bag',
  'box', 'batch', 'bowl', 'handful', 'pinch',
];

function pluralOf(s: string): string {
  if (s === 'leaf') return 'leaves';
  if (/[^aeiou]y$/.test(s)) return `${s.slice(0, -1)}ies`;
  if (/(s|x|z|ch|sh)$/.test(s)) return `${s}es`;
  return `${s}s`;
}

function agreeWithNumber(unit: string, value: number): string {
  const lower = unit.toLowerCase();
  const base = SINGULARS.find((s) => lower === s || lower === pluralOf(s));
  if (!base) return unit;
  // Anything at or below one takes the singular: "¼ cup", "1 tablespoon".
  const want = value <= 1 + 1e-6 ? base : pluralOf(base);
  return unit[0] === unit[0].toUpperCase() ? want[0].toUpperCase() + want.slice(1) : want;
}

const FRACS: [number, string][] = [
  [0, ''], [1 / 8, '⅛'], [1 / 4, '¼'], [1 / 3, '⅓'], [3 / 8, '⅜'], [1 / 2, '½'],
  [5 / 8, '⅝'], [2 / 3, '⅔'], [3 / 4, '¾'], [7 / 8, '⅞'], [1, ''],
];

export function formatQty(v: number): string {
  if (v <= 0) return '0';
  const whole = Math.floor(v + 1e-6);
  const frac = v - whole;
  let best = 0;
  let bestErr = Infinity;
  for (let i = 0; i < FRACS.length; i++) {
    const err = Math.abs(frac - FRACS[i][0]);
    if (err < bestErr) {
      bestErr = err;
      best = i;
    }
  }
  let w = whole;
  const fs = FRACS[best][1];
  if (FRACS[best][0] === 1) w += 1;
  if (!fs && w === 0) return '⅛';
  return fs ? (w ? `${w} ${fs}` : fs) : String(w);
}

// Spans inside parentheses are package sizes, not quantities to scale:
// "1 can (28 oz)" doubled is "2 cans (28 oz)", never "2 cans (56 oz)".
function parenSpans(s: string): [number, number][] {
  const spans: [number, number][] = [];
  let open = -1;
  for (let i = 0; i < s.length; i++) {
    if (s[i] === '(') open = i;
    else if (s[i] === ')' && open >= 0) {
      spans.push([open, i]);
      open = -1;
    }
  }
  return spans;
}

export function scaleAmount(
  amount: string,
  factor: number
): { text: string; scaled: boolean; approx: boolean } {
  if (factor === 1) return { text: amount, scaled: true, approx: false };
  // "1 per sandwich", "2 slices per sub", "1 lb per 4 servings", "5 oz each":
  // a rate, not a quantity. Doubling the batch doesn't change it.
  if (/\b(per|each|apiece)\b/i.test(amount)) return { text: amount, scaled: false, approx: false };
  const skip = parenSpans(amount);
  let any = false;
  let approx = false;

  const text = amount.replace(
    TOKEN,
    (
      match: string,
      mixed: string | undefined,
      frac: string | undefined,
      intUni: string | undefined,
      uni: string | undefined,
      dec: string | undefined,
      gap: string | undefined,
      unit: string | undefined,
      offset: number
    ) => {
      if (skip.some(([a, b]) => offset > a && offset < b)) return match;
      const numStr = mixed ?? frac ?? intUni ?? uni ?? dec;
      if (numStr == null) return match;
      any = true;
      const exact = tokenValue(numStr) * factor;
      const snapped = snapValue(exact, unit);
      if (Math.abs(snapped - exact) > 1e-6) approx = true;
      const word = unit ? agreeWithNumber(unit, snapped) : '';
      return formatQty(snapped) + (gap ?? '') + word;
    }
  );

  return any ? { text, scaled: true, approx } : { text: amount, scaled: false, approx: false };
}

// Batch sizes worth offering. This is a book of freezer batches — nobody makes
// 9 servings of soup — so the stepper moves between sensible sizes rather than
// one serving at a time. The recipe's own yield is always reachable.
const BATCHES = [2, 4, 6, 8, 10, 12, 16, 20, 24, 32];

export function batchSizes(original: number): number[] {
  return Array.from(new Set([...BATCHES, original]))
    .filter((n) => n >= 1 && n <= 64)
    .sort((a, b) => a - b);
}

export function prevBatch(original: number | null, current: number | null): number | null {
  if (original == null || current == null) return null;
  const sizes = batchSizes(original).filter((n) => n < current);
  return sizes.length ? sizes[sizes.length - 1] : null;
}

export function nextBatch(original: number | null, current: number | null): number | null {
  if (original == null || current == null) return null;
  return batchSizes(original).find((n) => n > current) ?? null;
}

// "8 servings" → 8, "about 48 meatballs — 12 servings" → 12 (the number
// that says servings wins over the first number in the string); null when
// there is no number to scale.
export function parseServings(yieldStr: string): number | null {
  const byWord = yieldStr.match(/(\d+)\s*servings?\b/i);
  if (byWord) return parseInt(byWord[1], 10);
  const m = yieldStr.match(/\d+/);
  return m ? parseInt(m[0], 10) : null;
}

// "8 servings" → "servings", "about 48 meatballs — 12 servings" → "servings",
// for rendering "<current> servings"
export function yieldUnit(yieldStr: string): string {
  if (/\d+\s*servings?\b/i.test(yieldStr)) return 'servings';
  return yieldStr.replace(/^\s*(about\s+)?\d+\s*/i, '') || 'servings';
}
